/**
 * Modal de Chat para o Kanban
 * 
 * Exibe a conversa completa em um modal, utilizando os mesmos componentes
 * da página Chat.tsx (ChatArea + ContactSidebar/GroupSidebar).
 * Sem duplicação de código - reutiliza componentes existentes.
 * 
 * Inclui controle de janela 24h para WhatsApp Oficial:
 * - Detecta quando janela está expirada (>24h sem resposta do cliente)
 * - Desabilita campos de envio de mensagem livre
 * - Habilita envio apenas via templates aprovados
 */

import { useState, useEffect, useCallback } from 'react';
// Detecta viewport mobile para exibir o chat em tela cheia
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ChatArea } from '@/components/tenant/chat/ChatArea';
import { ContactSidebar } from '@/components/tenant/chat/ContactSidebar';
import { GroupSidebar } from '@/components/tenant/chat/GroupSidebar';
import { SendTemplateDialog } from '@/components/tenant/chat/SendTemplateDialog';
import { useChatConversation } from '@/hooks/useChatConversation';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useSessionWindow } from '@/hooks/useSessionWindow';
import { usePendingTransfer } from '@/hooks/usePendingTransfer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { createErrorMetadata } from '@/lib/errorMetadata';
import { registerSendError } from '@/hooks/useErrorCatalog';

// Logger para debug do modal de chat do Kanban
const chatLogger = createLogger('ChatConversationModal');

interface ChatConversationModalProps {
  /** ID da conversa a ser exibida */
  conversationId: string | null;
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback quando o modal é fechado */
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal que exibe uma conversa completa do Kanban
 * Reutiliza ChatArea e ContactSidebar/GroupSidebar existentes
 * Inclui controle de janela 24h para WhatsApp Oficial
 */
export function ChatConversationModal({
  conversationId,
  open,
  onOpenChange,
}: ChatConversationModalProps) {
  // Hook de autenticação para obter usuário atual
  const { user } = useAuth();
  
  // Hook de permissões para verificar role do usuário
  const { userRole } = usePermissions();
  
  // Calcula flags de permissão para trava de input
  const isAdminOrSupervisor = userRole !== 'agent';
  const isTenantAdmin = userRole === 'tenant_admin' || userRole === 'root_admin';

  // Estado para controlar modal de envio de templates (janela 24h expirada)
  const isMobile = useIsMobile();
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  // [NOVO] Estado para modo read-only (inbox sem permissão de envio para o usuário)
  // Ativado quando a inbox da conversa não está na lista de inboxes acessíveis do agente
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);

  // ===== MODO OBSERVADOR =====
  // Permite supervisores/admins visualizarem conversas SEM marcar como lidas
  const [canUseObserverMode, setCanUseObserverMode] = useState(false);
  const [isObserverModeActive, setIsObserverModeActive] = useState(false);

  // ===== HUMAN TAKEOVER - PAUSA DE BOT =====
  // Estado para indicar quando está alternando pausa do bot
  const [isTogglingBotPause, setIsTogglingBotPause] = useState(false);

  // Hook que gerencia toda a lógica da conversa (incluindo paginação)
  const {
    conversation,
    messages,
    loading,
    messagesLoading,
    showSidebar,
    setShowSidebar,
    contactPresence,
    tenantId,
    handleSendMessage,
    handleSendImages,
    handleSendVideo,
    handleSendDocument,
    handleSendAudio,
    handleSendLocation,
    handleSendContact,
    handleSendReaction,
    handleDeleteMessage,
    handleUpdateStatus,
    handleClaimConversation,
    handleTyping,
    // Estados e funções de paginação para performance
    hasMoreMessages,
    loadingMoreMessages,
    totalMessagesCount,
    loadMoreMessages,
    // Função para atualizar dados da conversa (ex: após salvar tags)
    fetchConversation,
  } = useChatConversation({ 
    conversationId: open ? conversationId : null,
    isObserverMode: isObserverModeActive, // MODO OBSERVADOR: Não marca como lida
  });

  // Hook para calcular estado da janela 24h (WhatsApp Oficial)
  // Somente aplicável quando channel_type é 'whatsapp_oficial'
  const sessionWindow = useSessionWindow(
    conversation?.inbox?.channel_type,
    conversation?.last_inbound_at
  );

  // ===== HOOK PARA TRANSFERÊNCIA PENDENTE =====
  const {
    hasPendingTransfer,
    acceptTransferAndStart,
    accepting: isStartingConversation,
  } = usePendingTransfer(open ? conversationId : null);

  // Handler para iniciar atendimento
  const handleStartConversation = useCallback(async () => {
    const success = await acceptTransferAndStart();
    if (success) {
      toast.success('Atendimento iniciado!');
      fetchConversation();
    }
  }, [acceptTransferAndStart, fetchConversation]);

  // Handler para encerrar conversa
  // Após encerrar, fecha o modal automaticamente
  const handleCloseConversation = useCallback(async () => {
    if (!conversation) return;
    // Encerra a conversa via RPC server-side (valida tenant + ownership)
    const { error } = await supabase.rpc('close_conversation', {
      p_conversation_id: conversation.id,
      p_tenant_hint: tenantId,
    });
    if (error) {
      chatLogger.error('Erro ao encerrar conversa via RPC:', error);
      toast.error('Erro ao encerrar conversa');
      return;
    }
    toast.success('Conversa encerrada');
    // Fecha o modal após encerrar a conversa
    onOpenChange(false);
  }, [conversation, onOpenChange]);

  // ===== MODO OBSERVADOR - Busca configuração do tenant_members =====
  useEffect(() => {
    if (!open || !user?.id || !tenantId) return;
    
    const fetchObserverModeConfig = async () => {
      // RPC server-side valida tenant ativo e retorna a flag observer_mode_enabled
      const { data, error } = await supabase.rpc('get_user_observer_mode', {
        p_tenant_hint: tenantId,
      });
      if (error) {
        chatLogger.error('Erro ao buscar observer mode via RPC:', error);
        return;
      }
      const enabled = Boolean(data);
      setCanUseObserverMode(enabled);
      // Se modo observador habilitado, ativa por padrão ao abrir modal
      setIsObserverModeActive(enabled);
    };
    
    fetchObserverModeConfig();
  }, [open, user?.id, tenantId]);

  // [NOVO] ===== VERIFICAÇÃO DE PERMISSÃO DE INBOX (modo read-only) =====
  // Verifica se o usuário tem acesso à inbox da conversa carregada.
  // Usa a RPC get_user_accessible_inboxes (SECURITY DEFINER) para garantir
  // que a lista é autoritativa e não pode ser manipulada pelo cliente.
  // Se a inbox não estiver na lista acessível, isReadOnlyMode = true:
  // bloqueia envio de mensagens, transferências e encerramento.
  useEffect(() => {
    if (!open || !conversation?.inbox_id || !user?.id || !tenantId) return;

    const checkInboxPermission = async () => {
      chatLogger.info(`Verificando permissão de inbox: ${conversation.inbox_id}`);
      const { data, error } = await supabase.rpc('get_user_accessible_inboxes', {
        _user_id: user.id,
        _tenant_id: tenantId,
      });

      if (error) {
        chatLogger.error('Erro ao verificar permissão de inbox:', error);
        // Em caso de erro, mantém modo normal (não punitivo por falha de rede)
        setIsReadOnlyMode(false);
        return;
      }

      // v2.0.0: RPC agora retorna objetos com id+detalhes; extraímos apenas os ids
      const accessibleIds = ((data as Array<{ id: string }> | null) || []).map(r => r.id);
      const hasAccess = accessibleIds.includes(conversation.inbox_id);
      chatLogger.info(`Inbox acessível? ${hasAccess} | inbox_id: ${conversation.inbox_id}`);
      setIsReadOnlyMode(!hasAccess);
    };

    checkInboxPermission();
  }, [open, conversation?.inbox_id, user?.id, tenantId]);

  // ===== HUMAN TAKEOVER - Callback para pausar/reativar bot na conversa =====
  const handleToggleBotPause = useCallback(async () => {
    if (!conversation) return;
    
    setIsTogglingBotPause(true);
    try {
      const newPausedState = !conversation.bot_paused;

      // RPC server-side valida tenant + ownership da conversa antes do UPDATE
      const { error } = await supabase.rpc('set_conversation_bot_paused', {
        p_conversation_id: conversation.id,
        p_paused: newPausedState,
        p_tenant_hint: tenantId,
      });

      if (error) {
        chatLogger.error('Erro ao alternar pausa do bot:', error);
        toast.error('Erro ao alterar status do bot');
        return;
      }
      
      toast.success(newPausedState ? 'Bot pausado nesta conversa' : 'Bot reativado nesta conversa');
      
      // Atualiza dados da conversa para refletir mudança
      fetchConversation();
    } catch (error) {
      chatLogger.error('Erro ao alternar pausa do bot:', error);
      toast.error('Erro ao alterar status do bot');
    } finally {
      setIsTogglingBotPause(false);
    }
  }, [conversation, user?.id, fetchConversation]);

  /**
   * Handler para enviar mensagem de template (WhatsApp Oficial - janela 24h expirada)
   * Insere mensagem com message_type='template' e envia via Edge Function
   */
  const handleSendTemplateMessage = async (params: {
    template_name: string;
    template_language: string;
    template_params: Array<{ type: string; text?: string }>;
    /** Parâmetros para botões URL dinâmicos */
    button_params?: Array<{
      type: 'button';
      sub_type: 'url';
      index: number;
      parameters: Array<{ type: 'text'; text: string }>;
    }>;
    /** Parâmetros PIX para botões ORDER_DETAILS */
    order_details_params?: {
      pix_code: string;
      merchant_name: string;
      pix_key: string;
      pix_key_type: 'CNPJ' | 'CPF' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
      amount_value: number;
      item_name?: string;
      reference_id?: string;
    };
    /** Botões do template para visualização no chat */
    template_buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
    template_body: string;
  }) => {
    if (!conversation || !user) {
      chatLogger.warn('handleSendTemplateMessage: conversation ou user não disponíveis');
      return;
    }

    try {
      chatLogger.info('Enviando template:', params.template_name);

      // Monta o conteúdo de preview do template
      const previewContent = `📋 Template: ${params.template_name}`;

      // 1. Insere mensagem com status pending (inclui dados de botões para visualização)
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: previewContent,
          message_type: 'template',
          direction: 'outbound',
          status: 'pending',
          metadata: {
            template_name: params.template_name,
            template_language: params.template_language,
            template_params: params.template_params,
            template_body: params.template_body,
            // Dados de botões para visualização no chat
            template_buttons: params.template_buttons,
            button_params: params.button_params,
            order_details_params: params.order_details_params,
          },
        })
        .select()
        .single();

      if (insertError) {
        chatLogger.error('Erro ao inserir mensagem de template:', insertError);
        throw insertError;
      }

      chatLogger.info('Mensagem de template inserida, enviando via Edge Function...');

      // 2. Envia via Edge Function (inclui button_params e order_details_params)
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || '',
          message_type: 'template',
          template_name: params.template_name,
          template_language: params.template_language,
          template_params: params.template_params,
          button_params: params.button_params,
          order_details_params: params.order_details_params,
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: conversation.id,
          contact_id: conversation.contact?.id,
        },
      });

      // 3. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        chatLogger.error('Erro ao enviar template via Edge Function:', sendError || sendResult);
        
        // Extrai corpo real da resposta quando FunctionsHttpError (HTTP 4xx/5xx)
        let templateErrorDetails = sendResult;
        if (!templateErrorDetails && sendError?.context) {
          try {
            templateErrorDetails = await sendError.context.json();
          } catch {
            templateErrorDetails = { error: sendError?.message || 'Erro desconhecido' };
          }
        }
        
        await supabase.from('messages').update({ 
          status: 'failed',
          metadata: createErrorMetadata(sendError, templateErrorDetails)
        }).eq('id', messageData.id);
        
        // Registro assíncrono no catálogo de erros (fire-and-forget)
        registerSendError({
          errorCode: templateErrorDetails?.error_code || templateErrorDetails?.code || '500',
          provider: conversation?.inbox?.provider_slug || 'unknown',
          inboxId: conversation?.inbox_id,
          messageId: messageData.id,
          conversationId: conversation?.id,
          messageType: 'template',
          rawError: templateErrorDetails,
          providerMessage: templateErrorDetails?.error || templateErrorDetails?.details,
        });
        
        toast.error('Erro ao enviar template');
      } else {
        chatLogger.info('Template enviado com sucesso, external_id:', sendResult.external_id);
        await supabase.from('messages').update({ 
          status: 'sent', 
          external_id: sendResult.external_id 
        }).eq('id', messageData.id);
        setShowTemplateDialog(false);
        toast.success('Template enviado com sucesso!');
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar template:', error);
      toast.error('Erro ao enviar template');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn(
          "p-0 flex flex-col overflow-hidden",
          // Reserva espaço para o botão "X" do Dialog, evitando sobreposição
          // com o botão de expandir a sidebar no header do chat
          "[&_[data-chat-header-actions]]:pr-8 md:[&_[data-chat-header-actions]]:pr-10",
          // Mobile: tela cheia sem bordas para aproveitar todo o espaço
          isMobile
            ? "w-screen max-w-none h-[100dvh] max-h-[100dvh] rounded-none border-0"
            : "max-w-[95vw] w-[95vw] h-[94vh]",
        )}
        // Previne fechar ao clicar fora (UX melhor para chat)
        onInteractOutside={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        {/* Título oculto para acessibilidade */}
        <VisuallyHidden>
          <DialogTitle>Chat da Conversa</DialogTitle>
        </VisuallyHidden>
        
        {/* Loading state - exibe apenas enquanto carrega e conversation é null */}
        {loading && !conversation ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : conversation ? (
          <div className={cn("flex flex-1 min-h-0", isMobile ? "gap-0 p-0" : "gap-3 p-4")}>
            {/* Área principal do chat - wrapper apenas para estrutura flex, ChatArea gerencia seus estilos */}
            <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
              <ChatArea
                conversation={conversation}
                messages={messages}
                loading={messagesLoading}
                onSendMessage={handleSendMessage}
                onSendAudio={handleSendAudio}
                onSendImages={handleSendImages}
                onSendVideo={handleSendVideo}
                onSendDocument={handleSendDocument}
                onSendLocation={handleSendLocation}
                onSendContact={handleSendContact}
                onSendReaction={handleSendReaction}
                onDeleteMessage={handleDeleteMessage}
                onUpdateStatus={handleUpdateStatus}
                onToggleSidebar={() => setShowSidebar(!showSidebar)}
                showSidebar={showSidebar}
                onTyping={handleTyping}
                contactPresence={contactPresence}
                tenantId={tenantId}
                onClaimConversation={handleClaimConversation}
                currentUserId={user?.id}
                isAdminOrSupervisor={isAdminOrSupervisor}
                isTenantAdmin={isTenantAdmin}
                // Props de paginação para carregar mensagens antigas
                hasMoreMessages={hasMoreMessages}
                loadingMoreMessages={loadingMoreMessages}
                onLoadMoreMessages={loadMoreMessages}
                totalMessagesCount={totalMessagesCount}
                // Props para controle de janela 24h (WhatsApp Oficial)
                isSessionWindowExpired={sessionWindow.isExpired}
                onOpenTemplateDialog={() => setShowTemplateDialog(true)}
                // ===== MODO OBSERVADOR =====
                canUseObserverMode={canUseObserverMode}
                isObserverModeActive={isObserverModeActive}
                onToggleObserverMode={() => setIsObserverModeActive(!isObserverModeActive)}
                // ===== HUMAN TAKEOVER =====
                onToggleBotPause={handleToggleBotPause}
                isTogglingBotPause={isTogglingBotPause}
                // ===== CONTROLE DE TRANSFERÊNCIA PENDENTE =====
                hasPendingTransfer={hasPendingTransfer}
                onStartConversation={handleStartConversation}
                onCloseConversation={handleCloseConversation}
                isStartingConversation={isStartingConversation}
                // [NOVO] ===== MODO READ-ONLY (inbox sem permissão de envio) =====
                // Calculado via get_user_accessible_inboxes; bloqueia input e ações de header
                isReadOnlyMode={isReadOnlyMode}
              />
            </div>

            {/* Sidebar - Condicional para Grupo ou Contato - flutuante */}
            {showSidebar && (
              conversation.group_id && conversation.group ? (
                <GroupSidebar
                  group={conversation.group}
                  conversation={conversation}
                  onClose={() => setShowSidebar(false)}
                />
              ) : conversation.contact ? (
                <ContactSidebar
                  contact={conversation.contact}
                  conversation={conversation}
                  onClose={() => setShowSidebar(false)}
                  lastInboundAt={conversation.last_inbound_at}
                  onRefreshConversation={fetchConversation}
                  // ===== HUMAN TAKEOVER =====
                  onToggleBotPause={handleToggleBotPause}
                  isTogglingBotPause={isTogglingBotPause}
                  onNavigateToConversation={async (conversationId) => {
                    // No modal, fecha e reabre com a nova conversa
                    onOpenChange(false);
                    // Pequeno delay para fechar o modal atual antes de abrir outro
                    setTimeout(() => {
                      // Como estamos no Kanban, a navegação é feita pelo componente pai
                      // Por ora, apenas informamos ao usuário
                      toast.info('Navegue para o Chat para ver esta conversa');
                    }, 300);
                  }}
                />
              ) : null
            )}
          </div>
        ) : (
          /* Estado de fallback - aguardando conversation */
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
      </DialogContent>

      {/* Modal de seleção de template (janela 24h expirada - WhatsApp Oficial) */}
      <SendTemplateDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        inboxId={conversation?.inbox_id || ''}
        onSendTemplate={handleSendTemplateMessage}
      />
    </Dialog>
  );
}
